import { getMessage, createMessage } from './utils'
import type {
  CoreWebSocket,
  SocketClientMessageData,
  SocketClientMessageType,
  SocketServerMessage,
} from '@/services/socket/types'
import type { UpdateDataType } from '@/components/partials/display/VersionUpdate.vue'
import { useProjectStore } from '@/stores/project'
import { useOfficeStore } from '@/stores/office'
import { useContentStore } from '@/stores/content'
import { useProjectCalculator } from '@/stores/projectCenter/projectCalculator'
import { DialogEvent, useDialogBus } from '@/helpers/event-bus'
import i18n from '@/i18n'
import { useOfficeCalculator } from '@/stores/officeCenter/officeCalculator'
import * as Sentry from '@sentry/vue'
import type { SocketReadyData } from '@arcexperts/core-types'
import { TIER_BOUNDARY_CHOICE_ENABLED } from '@/helpers/tier-boundary'

type ProjectCalculationPayload = SocketClientMessageData<'get_project_calculation'>

interface ProjectCalculationQueueEntry {
  inFlight: ProjectCalculationPayload
  pending: ProjectCalculationPayload | null
}

export type ProjectImportValidationResult =
  | { ok: true; project: Record<string, unknown>; updates: UpdateDataType[] }
  | { ok: false; reason: 'validation_failed' | 'timeout' | 'busy'; message?: string }

interface PendingProjectImportValidation {
  project: Record<string, unknown> | null
  updates: UpdateDataType[]
  resolve: (result: ProjectImportValidationResult) => void
  timer: ReturnType<typeof setTimeout> | null
}

const PROJECT_IMPORT_VALIDATION_TIMEOUT_MS = 15000
// The server sends `update_available` right after `inject_data` when versions differ;
// wait a short grace period for it so the import flow can react deterministically.
const PROJECT_IMPORT_UPDATE_GRACE_MS = 250

class SocketDialogError extends Error {
  constructor(
    public readonly errorType: string,
    message?: unknown
  ) {
    super(String(message) || errorType)
    this.name = 'SocketDialogError'
  }
}

// Typed translation helper to avoid vue-i18n type complexity
// eslint-disable-next-line @typescript-eslint/no-explicit-any
const t = (key: string): string => (i18n.global as any).t(key)

export class MessageCenter {
  private projectStore = useProjectStore()
  private officeStore = useOfficeStore()
  private contentStore = useContentStore()
  private projectCalculator = useProjectCalculator()
  private officeCalculator = useOfficeCalculator()
  private ws: CoreWebSocket
  private data: Partial<SocketReadyData>
  private widgetTextsRetryCount: Map<string, number> = new Map()
  private projectCalculationQueue: Map<string, ProjectCalculationQueueEntry> = new Map()
  private pendingProjectImport: PendingProjectImportValidation | null = null
  private status = 'off'
  public heartbeat: ReturnType<typeof setInterval> | number = 0
  public isAlive = -1

  constructor(ws: CoreWebSocket, data: Partial<SocketReadyData> = {}) {
    this.ws = ws
    this.data = data
  }

  public setStatus(status: string) {
    /* available stati: off, connected, reconnected, closed */
    this.status = status
  }
  public getStatus() {
    return this.status
  }
  public startHeartbeat() {
    this.heartbeat = setInterval(() => {
      this.ws.send(createMessage('ping', null))
    }, 20 * 1000)
  }

  public close() {
    clearInterval(this.heartbeat)
    this.projectCalculationQueue.clear()
    this.setStatus('closed')
  }

  public showErrorDialog(errType: string, errMessage?: unknown) {
    if (import.meta.env.VITE_SENTRY_ENABLED !== 'true') {
      console.error('[Socket Dialog Error]', errType, errMessage)
    }
    Sentry.captureException(new SocketDialogError(errType, errMessage), {
      fingerprint: ['socket', 'dialog', errType],
      tags: { error_dialog: errType },
    })
    useDialogBus().emit(DialogEvent.SHOW_ERROR, {
      title: t(`errors.api.${errType}.title`),
      text: t(`errors.api.${errType}.message`),
      confirm: t('general.button.reload'),
      reload: true,
    })
  }
  public hideDialog() {
    useDialogBus().emit(DialogEvent.FORCE_QUITE)
  }

  handleMessage(message: MessageEvent) {
    const messageObj = getMessage(message)
    if (messageObj.type === 'string') {
      Sentry.logger.debug('Received string message', { data: String(messageObj.data) })
      return
    }

    switch (messageObj.type) {
      case 'socket_is_ready':
        this.onSocketIsReady(messageObj)
        break
      case 'help_text':
        this.onHelpText(messageObj)
        break
      case 'widget_texts':
        this.onWidgetTexts(messageObj)
        break
      case 'building_types':
        this.onBuildingTypes(messageObj)
        break
      case 'widget_texts_error':
        this.onWidgetTextsError(messageObj)
        break
      case 'language_changed':
        this.onLanguageChanged(messageObj)
        break
      case 'default_data':
        void this.onDefaultData(messageObj)
        break
      case 'inject_data':
        void this.onInjectData(messageObj)
        break
      case 'project_calculation_result':
        this.onProjectCalculationResult(messageObj)
        break
      case 'office_calculation_result':
        this.onOfficeCalculationResult(messageObj)
        break
      case 'update_available':
        this.onUpdateAvailable(messageObj)
        break
      case 'pong':
        this.onPong(messageObj)
        break
      case 'error':
        this.onError(messageObj)
        break
      default:
        this.onKnownMessageType(messageObj)
    }
  }

  /* TRIGGERS */
  getProjectDefaultObject(part: string, request: string) {
    this.ws.send(createMessage('get_project_default_object', { part, request }))
  }
  triggerProjectCalculation(
    part: SocketClientMessageData<'get_project_calculation'>['part'],
    request: SocketClientMessageData<'get_project_calculation'>['request'],
    data: SocketClientMessageData<'get_project_calculation'>['values']
  ) {
    const payload = {
      part,
      request,
      values: data,
    } as SocketClientMessageData<'get_project_calculation'>
    const version = this.projectStore.getVersion(part)
    if (typeof version === 'string') {
      payload.version = version
    }
    if (request === 'minutesPerSquareMeterForUsages') {
      // VAL-160: opt in to the per-usage category reference flags (see core-types).
      payload.categoryReference = true
      // VAL-102: and to the area interval boundary info, while the manual choice is enabled.
      if (TIER_BOUNDARY_CHOICE_ENABLED) payload.tierBoundaries = true
    }
    const queueKey = this.getProjectCalculationQueueKey(part, request)
    const queueEntry = this.projectCalculationQueue.get(queueKey)
    if (queueEntry) {
      queueEntry.pending = payload
      return
    }

    this.projectCalculationQueue.set(queueKey, {
      inFlight: payload,
      pending: null,
    })
    this.sendProjectCalculation(payload)
  }
  private sendProjectCalculation(payload: ProjectCalculationPayload) {
    Sentry.addBreadcrumb({
      message: `Project calculation: ${payload.part}/${payload.request}`,
      category: 'calculation',
      level: 'info',
      data: { part: payload.part, request: payload.request },
    })
    this.ws.send(createMessage('get_project_calculation', payload))
  }
  private getProjectCalculationQueueKey(part: string, request: string): string {
    return `${part}:${request}`
  }
  private completeProjectCalculation(part: string, request: string): boolean {
    const queueKey = this.getProjectCalculationQueueKey(part, request)
    const queueEntry = this.projectCalculationQueue.get(queueKey)
    if (!queueEntry) {
      return true
    }

    if (queueEntry.pending) {
      const pending = queueEntry.pending
      queueEntry.inFlight = pending
      queueEntry.pending = null
      this.sendProjectCalculation(pending)
      return false
    }

    this.projectCalculationQueue.delete(queueKey)
    return true
  }
  triggerOfficeCalculation(
    part: SocketClientMessageData<'get_office_calculation'>['part'],
    request: SocketClientMessageData<'get_office_calculation'>['request'],
    data: SocketClientMessageData<'get_office_calculation'>['values']
  ) {
    Sentry.addBreadcrumb({
      message: `Office calculation: ${part}/${request}`,
      category: 'calculation',
      level: 'info',
      data: { part, request },
    })
    const version = this.officeStore.getVersion
    const payload = {
      part,
      request,
      values: data,
      version,
    } as SocketClientMessageData<'get_office_calculation'>
    this.ws.send(createMessage('get_office_calculation', payload))
  }
  triggerMessage<Type extends SocketClientMessageType>(type: Type, data: SocketClientMessageData<Type>) {
    this.ws.send(createMessage(type, data))
  }

  /**
   * Runs an imported project through the server-side schema validation that is
   * also used for host-injected data. While the request is pending, the
   * resulting `inject_data`, `update_available` and `error` messages are routed
   * to the returned promise instead of the regular store handlers.
   */
  validateProjectImport(
    meta: Record<string, unknown>,
    project: Record<string, unknown>
  ): Promise<ProjectImportValidationResult> {
    if (this.pendingProjectImport) {
      return Promise.resolve({ ok: false, reason: 'busy' })
    }
    return new Promise((resolve) => {
      const pending: PendingProjectImportValidation = {
        project: null,
        updates: [],
        timer: null,
        resolve: (result) => {
          if (pending.timer) clearTimeout(pending.timer)
          this.pendingProjectImport = null
          resolve(result)
        },
      }
      pending.timer = setTimeout(
        () => pending.resolve({ ok: false, reason: 'timeout' }),
        PROJECT_IMPORT_VALIDATION_TIMEOUT_MS
      )
      this.pendingProjectImport = pending
      this.triggerMessage('validate_injected_data', { meta, project })
    })
  }

  private finishPendingProjectImport(pending: PendingProjectImportValidation) {
    if (!pending.project) return
    pending.resolve({ ok: true, project: pending.project, updates: pending.updates })
  }

  private widgetTextRetryKey(slug: string, role?: string): string {
    return role ? `${slug}:${role}` : slug
  }

  private requestLocalizedContent() {
    const baseSlugs = [
      'widget-texts-project-general',
      'widget-texts-project-setup',
      'widget-texts-project-service',
      'widget-texts-project-summary',
      'widget-texts-general',
      'widget-texts-office',
    ]
    const roleScopedSlugs = [
      'widget-texts-project-general',
      'widget-texts-project-setup',
      'widget-texts-project-service',
    ]
    const roles = [
      'ARCHITECT',
      'CIVIL_ENGINEER',
      'CIVIL_ENGINEER_EXCAVATION',
      'ELECTRICAL',
      'HEATING_COOLING',
      'VENTILATION_AC',
      'SANITARY',
      'BUILDING_AUTOMATION',
      'SPECIALIST_COORDINATION',
      'LANDSCAPE_ARCHITECT',
      'ENGINEER',
      'HVAC',
    ]

    for (const slug of baseSlugs) {
      this.triggerMessage('get_widget_texts', { slug })
    }
    for (const slug of roleScopedSlugs) {
      for (const role of roles) {
        this.triggerMessage('get_widget_texts', { slug, role })
      }
    }

    this.triggerMessage('get_building_types', null)
  }

  public refetchLocalizedContent() {
    this.widgetTextsRetryCount.clear()
    this.contentStore.clearLocalizedContent()
    this.requestLocalizedContent()
  }

  /* CALLBACKS */
  onPong = (message: SocketServerMessage<'pong'>) => {
    this.isAlive = message.timestamp
  }
  onKnownMessageType = (message: SocketServerMessage) => {
    Sentry.logger.warn('Unknown message type received', { type: message.type })
    // this.ws.send(createMessage('error', 'unknown message type'))
  }
  onSocketIsReady = (message: SocketServerMessage<'socket_is_ready'>) => {
    const socketData = message.data
    this.data = socketData
    this.contentStore.initComplexityStepSizes(socketData.complexityStepSizes)
    this.contentStore.coordinationSettings = socketData.coordinationSettings
    if (!socketData.refreshed) {
      window.parent.postMessage({ type: 'init_success' }, socketData.origin)
    }
    this.requestLocalizedContent()
  }
  onHelpText = (message: SocketServerMessage<'help_text'>) => {
    const data = message.data
    this.contentStore.initHelpText(data.slug, data.title, data.body, data.role)
  }
  onWidgetTexts = (message: SocketServerMessage<'widget_texts'>) => {
    const data = message.data
    this.contentStore.initWidgetTexts(data.slug, data.texts, data.role)
    // Reset retry count on success
    this.widgetTextsRetryCount.delete(this.widgetTextRetryKey(data.slug, data.role))
  }
  onBuildingTypes = (message: SocketServerMessage<'building_types'>) => {
    const data = message.data
    this.contentStore.initBuildingTypes(data.items ?? [])
    this.projectStore.normalizeGeneralBuildingTypes()
  }
  onWidgetTextsError = (message: SocketServerMessage<'widget_texts_error'>) => {
    const data = message.data
    const retryKey = this.widgetTextRetryKey(data.slug, data.role)
    const currentRetry = this.widgetTextsRetryCount.get(retryKey) ?? 0
    const maxRetries = 5
    const delays = [5000, 10000, 20000, 40000, 60000]

    if (currentRetry >= maxRetries) {
      if (import.meta.env.VITE_SENTRY_ENABLED !== 'true') {
        console.error('[Widget Texts] Max retries reached for', data.slug)
      }
      Sentry.captureException(new Error(`Widget texts failed after ${maxRetries} retries: ${data.slug}`))
      return
    }

    this.widgetTextsRetryCount.set(retryKey, currentRetry + 1)
    const delay = delays[currentRetry]
    Sentry.logger.warn('Widget texts fetch failed, scheduling retry', {
      slug: data.slug,
      attempt: currentRetry + 1,
      nextRetryMs: delay,
    })

    setTimeout(() => {
      this.triggerMessage('get_widget_texts', { slug: data.slug, role: data.role ?? null })
    }, delay)
  }
  onLanguageChanged = (message: SocketServerMessage<'language_changed'>) => {
    const data = message.data
    if (!data?.language) {
      return
    }
    this.data = {
      ...this.data,
      lang: data.language,
    }
    Sentry.logger.info('Socket language changed', { language: data.language })
  }
  private async initializeData(
    message: SocketServerMessage<'default_data'> | SocketServerMessage<'inject_data'>,
    source: string
  ) {
    const data = message.data
    Sentry.logger.debug('Initializing data', {
      source,
      hasProject: !!data?.project,
      hasOffice: !!data?.architect_office,
    })
    if (data?.project) {
      await this.projectStore.initProject(data)
    }
    if (data?.architect_office) {
      await this.officeStore.initOffice(data)
    }
    this.ws.dataIsReady = true
  }
  onDefaultData = async (message: SocketServerMessage<'default_data'>) => {
    await this.initializeData(message, 'onDefaultData')
  }
  onInjectData = async (message: SocketServerMessage<'inject_data'>) => {
    const pending = this.pendingProjectImport
    if (pending) {
      const project = (message.data as { project?: Record<string, unknown> } | undefined)?.project
      if (!project) {
        pending.resolve({ ok: false, reason: 'validation_failed' })
        return
      }
      pending.project = project
      if (pending.timer) clearTimeout(pending.timer)
      pending.timer = setTimeout(() => this.finishPendingProjectImport(pending), PROJECT_IMPORT_UPDATE_GRACE_MS)
      return
    }
    await this.initializeData(message, 'onInjectData')
  }
  onProjectCalculationResult = (message: SocketServerMessage<'project_calculation_result'>) => {
    const data = message.data
    Sentry.setContext('last_calculation', {
      type: 'project',
      part: data.part,
      request: data.request,
      value: data.value,
      timestamp: message.timestamp,
    })
    if (!this.completeProjectCalculation(data.part, data.request)) {
      return
    }
    this.projectCalculator.setResult(data)
  }
  onOfficeCalculationResult = (message: SocketServerMessage<'office_calculation_result'>) => {
    const data = message.data
    Sentry.setContext('last_calculation', {
      type: 'office',
      part: data.part,
      request: data.request,
      value: data.value,
      timestamp: message.timestamp,
    })
    this.officeCalculator.setResult(data)
  }
  onUpdateAvailable = (message: SocketServerMessage<'update_available'>) => {
    Sentry.logger.info('Update available', { data: JSON.stringify(message.data) })

    const pending = this.pendingProjectImport
    if (pending?.project) {
      pending.updates = message.data as UpdateDataType[]
      this.finishPendingProjectImport(pending)
      return
    }

    /* both stores can listen, they will react only on update parts they are responsible for */
    this.projectStore.setAvailableUpdates(message.data as UpdateDataType[])
    this.officeStore.setAvailableUpdates(message.data as UpdateDataType[])
  }
  onError = (message: SocketServerMessage<'error'>) => {
    const data = message.data
    const pending = this.pendingProjectImport
    if (pending && data?.type === 'validate_injected_data') {
      Sentry.logger.warn('Project import rejected by validation', { message: String(data.message ?? '') })
      pending.resolve({ ok: false, reason: 'validation_failed', message: String(data.message ?? '') })
      return
    }
    const severeHandledErrors = ['validate_injected_data', 'default_data', 'connection_error']
    if (severeHandledErrors.includes(data?.type)) {
      this.setStatus('error')
      if (import.meta.env.VITE_SENTRY_ENABLED !== 'true') {
        console.error('[Socket Severe Error]', data.type, data.message)
      }
      Sentry.captureException(new Error(`Severe socket error: ${data.type} - ${data.message}`))
      useDialogBus().emit(DialogEvent.SHOW_ERROR, {
        title: t('errors.client.' + data.type + '.title'),
        text: t('errors.client.' + data.type + '.message') + ' ' + (data.message || ''),
        level: 'severe',
      })
    } else {
      if (import.meta.env.VITE_SENTRY_ENABLED !== 'true') {
        console.error('[Socket Error]', data.type, data.message)
      }
      Sentry.captureException(new Error(`Error from socket: ${data.type} - ${data.message}`))
      useDialogBus().emit(DialogEvent.SHOW_ERROR, {
        title: data?.type || data?.name || t('general.error_fallback_title'),
        text: data.message || null,
        confirm: t('general.button.reload'),
        cancel: t('general.button.cancel'),
        reload: true,
      })
    }
  }
}
